import { promisify } from "util";

import { makeApolloClient } from "@ticketingplatform/api/dist/config/apollo";
import { Auth } from "aws-amplify";
import isSafeInteger from "lodash/isSafeInteger";
// eslint-disable-next-line import/no-extraneous-dependencies
import { CognitoUser } from "amazon-cognito-identity-js";
import { setContext } from "@apollo/client/link/context";
import { Venue } from "@ticketingplatform/api/dist/venue/__generated__/Venue";

import environment from "./environment";
import { LOCAL_STORAGE_VENUE_KEY } from "./consts";

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const defaultHeaders: { Authorization: string } | { "x-api-key": string } =
    environment.LOCAL_TOKEN
      ? {
          Authorization: environment.LOCAL_TOKEN,
        }
      : {
          "x-api-key": environment.APPSYNC_KEY,
        };

  try {
    const currentUser = (await Auth.currentAuthenticatedUser()) as CognitoUser;
    const getCurrentUserSession = promisify(
      currentUser.getSession.bind(currentUser)
    );
    let userSession = await getCurrentUserSession();

    if (!userSession) {
      return defaultHeaders;
    }

    const expiration: number = userSession.getAccessToken().payload.exp;

    if (!isSafeInteger(expiration)) {
      return defaultHeaders;
    }

    if (expiration * 1000 < Date.now() + 60 * 1000) {
      await promisify(currentUser.refreshSession.bind(currentUser))(
        userSession.getRefreshToken()
      );
    }

    userSession = await getCurrentUserSession();

    return !userSession
      ? defaultHeaders
      : {
          Authorization: userSession.getAccessToken().getJwtToken(),
        };
  } catch {
    return defaultHeaders;
  }
};

const getVenueHeaders = () => {
  if (typeof window === "undefined") {
    return {};
  }

  const venueJson = window.localStorage.getItem(LOCAL_STORAGE_VENUE_KEY);

  if (!venueJson) {
    return {};
  }

  const venue: Venue | undefined = JSON.parse(venueJson);

  if (!venue) {
    return {};
  }

  return { "x-venue-id": venue.id };
};

const authLink = setContext(async (operation, prevContext) => {
  const authHeaders = await getAuthHeaders();

  const context = {
    ...operation.context,
    ...prevContext,
  };

  return {
    ...(context || {}),
    headers: {
      ...(context?.headers || {}),
      ...authHeaders,
    },
  };
});

const venueLink = setContext((operation, prevContext) => {
  const venueHeaders = getVenueHeaders();

  const context = {
    ...operation.context,
    ...prevContext,
  };

  return {
    ...(context || {}),
    headers: {
      ...(context?.headers || {}),
      ...venueHeaders,
    },
  };
});

export const apolloClient = makeApolloClient([authLink, venueLink]);
